// stores/useWebSocketStore.js
import { create } from 'zustand';
import { subscribeWithSelector } from 'zustand/middleware';
import { io } from 'socket.io-client';
import { API_BASEURL } from '../config/axiosInstance';

const useWebSocketStore = create(
    subscribeWithSelector((set, get) => ({
        // State
        socket: null,
        connected: false,
        connecting: false,
        error: null,
        reconnectCount: 0,

        // Configuration
        config: {
            url: API_BASEURL,
            reconnectAttempts: 5,
            reconnectDelay: 1000,
            autoConnect: true,
        },

        // Actions
        setConfig: (newConfig) => set((state) => ({
            config: { ...state.config, ...newConfig }
        })),

        connect: () => {
            const { socket, connected, connecting, config } = get();

            if (socket?.connected || connected) {
                console.warn('WebSocket is already connected');
                return;
            }

            if (connecting) {
                console.warn('WebSocket is already connecting');
                return;
            }

            set({ connecting: true, error: null });

            const newSocket = io(config.url, {
                transports: ['websocket', 'polling'],
                upgrade: true,
                autoConnect: false
            });

            newSocket.connect();

            // Connection successful
            newSocket.on('connect', () => {
                console.log('WebSocket connected');
                set({
                    socket: newSocket,
                    connected: true,
                    connecting: false,
                    error: null,
                    reconnectCount: 0
                });
            });

            // Connection error
            newSocket.on('connect_error', (error) => {
                console.error('WebSocket connection error:', error);
                const currentState = get();

                set({
                    connected: false,
                    connecting: false,
                    error: error.message
                });

                // Auto-reconnect logic
                if (currentState.reconnectCount < config.reconnectAttempts) {
                    const newReconnectCount = currentState.reconnectCount + 1;
                    console.log(`Attempting to reconnect (${newReconnectCount}/${config.reconnectAttempts})...`);

                    set({ reconnectCount: newReconnectCount });

                    setTimeout(() => {
                        get().connect();
                    }, config.reconnectDelay);
                } else {
                    console.error('Max reconnection attempts reached');
                }
            });

            // Disconnection
            newSocket.on('disconnect', (reason) => {
                console.log('WebSocket disconnected:', reason);
                set({
                    connected: false,
                    connecting: false,
                    error: null
                });

                // Auto-reconnect on unexpected disconnection
                if (reason === 'io server disconnect') {
                    setTimeout(() => {
                        get().connect();
                    }, config.reconnectDelay);
                }
            });

            set({ socket: newSocket });
        },

        disconnect: () => {
            const { socket } = get();

            if (socket) {
                socket.disconnect();
                socket.removeAllListeners();
            }

            set({
                socket: null,
                connected: false,
                connecting: false,
                error: null,
                reconnectCount: 0
            });
        },

        emit: (event, data) => {
            const { socket, connected } = get();

            if (socket?.connected || connected) {
                socket.emit(event, data);
            } else {
                console.warn('Cannot emit: WebSocket is not connected');
            }
        },

        // Event listener management
        on: (event, callback) => {
            const { socket } = get();
            if (socket) {
                socket.on(event, callback);
            }
        },

        off: (event, callback) => {
            const { socket } = get();
            if (socket) {
                if (callback) {
                    socket.off(event, callback);
                } else {
                    socket.removeAllListeners(event);
                }
            }
        },

        // Utility methods
        isReady: () => {
            const { socket, connected } = get();
            return socket?.connected || connected;
        },

        getConnectionStatus: () => {
            const { connected, connecting, error } = get();
            return { connected, connecting, error };
        }
    }))
);

export default useWebSocketStore;
